/**
 * Polling data hook — the onSnapshot replacement (design §5.5).
 *
 * Calls `fetcher` immediately, then re-runs it every `intervalMs` (default 10s).
 * Returns { data, error, loading, refresh } — `refresh()` revalidates on demand
 * (use it after a mutation: revalidate-after-mutation). The interval pauses
 * while the tab is hidden and fires once on re-show, so a backgrounded tab does
 * not hammer the Worker.
 *
 * `fetcher` identity controls re-subscription: wrap it in useCallback so it is
 * stable, or include its closed-over inputs in the `deps` array (e.g. a selected
 * ticket id) to tear down + restart cleanly when they change.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface PollResult<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  refresh: () => void;
}

export function usePoll<T>(
  fetcher: () => Promise<T>,
  intervalMs = 10_000,
  deps: ReadonlyArray<unknown> = [],
): PollResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  // Keep the latest fetcher in a ref so the interval doesn't restart on every
  // render — only the explicit `deps` (and intervalMs) drive re-subscription.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const aliveRef = useRef(true);

  const run = useCallback(async () => {
    try {
      const next = await fetcherRef.current();
      if (aliveRef.current) {
        setData(next);
        setError(null);
      }
    } catch (e) {
      if (aliveRef.current) setError(e);
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    setLoading(true);
    void run();

    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer === null) timer = setInterval(() => void run(), intervalMs);
    };
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        void run();
        start();
      }
    };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      aliveRef.current = false;
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, intervalMs, ...deps]);

  return { data, error, loading, refresh: run };
}
